import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI, customerAPI } from '../services/api';
import type { Project, Customer } from '../types';

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    customerOrPo: '', // Combined: '' = all, 'no_po' = no POs, or customer ID
    is_active: '',
  });

  useEffect(() => {
    fetchCustomers();
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data.results);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const params: any = {};
      
      // Handle combined customer/PO filter
      if (filters.customerOrPo === 'no_po') {
        params.has_pos = 'false';
      } else if (filters.customerOrPo) {
        params.customer = filters.customerOrPo;
      }
      
      if (filters.is_active) params.is_active = filters.is_active;
      
      const response = await projectAPI.getAll(params);
      setProjects(response.data.results);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ customerOrPo: '', is_active: '' });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectAPI.delete(id);
        fetchProjects();
      } catch (error) {
        console.error('Failed to delete project:', error);
        alert('Failed to delete project');
      }
    }
  };

  if (loading) return <div className="container">Loading...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Projects</h2>
          <Link to="/projects/new" className="btn btn-primary">
            Create New Project
          </Link>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 250px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Filter by Company / PO Status
            </label>
            <select
              value={filters.customerOrPo}
              onChange={(e) => setFilters({ ...filters, customerOrPo: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Projects</option>
              <option value="no_po">No PO</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Status
            </label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {projects.length === 0 ? (
          <p>No projects found. {filters.customerOrPo || filters.is_active ? 'Try adjusting your filters.' : 'Create your first project to get started.'}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Billing Rates</th>
                  <th>POs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td><strong>{project.code}</strong></td>
                    <td>{project.name}</td>
                    <td style={{ maxWidth: '300px' }}>{project.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${project.is_active ? 'active' : 'inactive'}`}>
                        {project.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{project.billing_rates_count || 0}</td>
                    <td>{project.purchase_orders_count || 0}</td>
                    <td className="actions">
                      <Link to={`/projects/${project.id}/edit`} className="btn btn-secondary btn-small">
                        Edit
                      </Link>
                      <button
                        onClick={() => !project.has_time_entries && handleDelete(project.id!)}
                        className="btn btn-danger btn-small"
                        disabled={project.has_time_entries}
                        style={{
                          opacity: project.has_time_entries ? 0.5 : 1,
                          cursor: project.has_time_entries ? 'not-allowed' : 'pointer',
                        }}
                        title={
                          project.has_time_entries
                            ? `Cannot delete: ${project.time_entries_count} time ${project.time_entries_count === 1 ? 'entry' : 'entries'} exist for this project's billing rates`
                            : 'Delete this project (will also delete associated billing rates and user assignments)'
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectList;

